import React from "react";
import { Package, Calendar, FileText, Plus, ShoppingCart, Box } from "lucide-react";
import {
  CreateChildPurchaseOrderFormAction,
  CreateChildPurchaseOrderFormData,
  CreateChildPurchaseOrderFormDataKey,
  ChildPOProductInputField,
  PurchaseOrder
} from "src/types";
import { ValidationErrors } from "src/errors";
import { useScrollOnMessage } from "src/hooks";
import { ChildPOProductInputTable } from "src/components/tables/inputTables";
import { ParentPOProductsTable } from "src/components/tables";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { DateInputBox, TextInputBox, TextInputBoxLarge } from "src/components/ui/inputs";
import { SubmissionTooltip } from "src/components/tooltips";
import { Tooltip } from "src/components/ui/tooltip";
import { IdGenerator } from "src/utils";

interface CreateChildPurchaseOrderFormProps {
  formData: CreateChildPurchaseOrderFormData;
  parentPurchaseOrder?: PurchaseOrder;
  validationErrors?: ValidationErrors<CreateChildPurchaseOrderFormDataKey>;
  formLvlErrorMessage: string;
  isFormComplete: boolean;
  handleInputChange: (
    field: CreateChildPurchaseOrderFormDataKey,
    value: CreateChildPurchaseOrderFormData[keyof CreateChildPurchaseOrderFormData]
  ) => void;
  handleSubmit: (e?: React.FormEvent) => void;
  setAction: (action: CreateChildPurchaseOrderFormAction) => void;
}

const CreateChildPurchaseOrderForm: React.FC<CreateChildPurchaseOrderFormProps> = ({
  formData,
  parentPurchaseOrder,
  validationErrors,
  formLvlErrorMessage,
  isFormComplete,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const handleSetProducts: React.Dispatch<React.SetStateAction<ChildPOProductInputField[]>> = (updatedProducts) => {
    handleInputChange("products", updatedProducts as ChildPOProductInputField[]);
  };

  const handleAddNewProduct = () => {
    const newProduct: ChildPOProductInputField = {
      fieldId: IdGenerator.generateUniqueElementId(),
      id: "",
      ukQuantity: "0",
      euQuantity: "0"
    };
    handleSetProducts([...formData.products, newProduct]);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Create Child Purchase Order</h1>
            <p className="text-gray-300 mt-2 text-lg">Create a new child PO from confirmed parent order</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Search Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  Parent Purchase Order Search
                </h3>
                <TextInputBox
                  id="parent-purchase-order-id"
                  placeholder="Parent Purchase Order ID"
                  onChange={(value) => {
                    handleInputChange("parentPurchaseOrderId", value.toUpperCase());
                  }}
                  value={formData.parentPurchaseOrderId}
                />
                <ValidationErrorMessage
                  message={validationErrors?.getErrorMessageByFormDataKey("parentPurchaseOrderId")}
                />
              </div>

              {parentPurchaseOrder && (
                <>
                  {/* Parent PO Info Section */}
                  <div className="border border-primary-200 rounded-2xl p-6 bg-primary-50">
                    <h3 className="text-sm font-semibold text-primary-900 mb-4 tracking-wide uppercase flex items-center">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Parent Purchase Order Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <span className="block text-xs font-medium text-primary-700 mb-1">PO ID</span>
                        <span className="block text-sm font-semibold text-primary-900">{parentPurchaseOrder.id}</span>
                      </div>
                      <div>
                        <span className="block text-xs font-medium text-primary-700 mb-1">Vendor</span>
                        <span className="block text-sm font-semibold text-primary-900">
                          {parentPurchaseOrder.vendorName}
                        </span>
                      </div>
                      <div>
                        <span className="block text-xs font-medium text-primary-700 mb-1">Confirmation Status</span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-success-100 text-success-800">
                          {parentPurchaseOrder.confirmationStatus}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Parent PO Products Table (Non-editable) */}
                  <div className="border border-neutral-200 rounded-2xl p-6 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-black tracking-wide uppercase flex items-center">
                        <Box className="w-4 h-4 mr-2" />
                        Parent PO Products
                      </h3>
                      <span className="text-xs font-medium text-neutral-500 bg-neutral-100 px-3 py-1 rounded-full">
                        {parentPurchaseOrder.products?.length || 0} Products
                      </span>
                    </div>
                    <ParentPOProductsTable
                      products={parentPurchaseOrder.products}
                      productConfirmationData={parentPurchaseOrder.confirmationDetails?.products}
                    />
                  </div>

                  {/* Child PO Products Section (Editable) */}
                  <div className="border border-neutral-200 rounded-2xl p-6 bg-white">
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-black tracking-wide uppercase flex items-center">
                        <FileText className="w-4 h-4 mr-2" />
                        Child PO Products
                      </h3>
                      <p className="text-xs text-neutral-600 mt-1">
                        Add products for the new child purchase order. Enter product ID with UK quantity (sent to GXO)
                        and EU quantity - remaining details will be fetched from Shopify.
                      </p>
                    </div>

                    {formData.products.length > 0 ? (
                      <>
                        <ChildPOProductInputTable products={formData.products} setProducts={handleSetProducts} />
                        <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("products")} />
                      </>
                    ) : (
                      <div className="bg-neutral-50 border border-dashed border-neutral-300 rounded-lg p-8 text-center">
                        <Package className="w-12 h-12 mx-auto mb-3 text-neutral-400" />
                        <p className="text-sm text-neutral-600 mb-4">No products added yet</p>
                        <p className="text-xs text-neutral-500">
                          Click the button below to add products to this child PO
                        </p>
                      </div>
                    )}

                    <div className="mt-4">
                      <button
                        className="flex items-center space-x-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium shadow-sm"
                        onClick={handleAddNewProduct}
                        type="button"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add Product</span>
                      </button>
                    </div>
                  </div>

                  {/* Delivery Date Section */}
                  <div className="border border-neutral-200 rounded-2xl p-6 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      Delivery Information
                    </h3>
                    <DateInputBox
                      id="estimated-delivery-date"
                      label="Estimated Delivery Date"
                      onChange={(value) => handleInputChange("estDeliveryDate", value)}
                      value={formData.estDeliveryDate}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("estDeliveryDate")}
                    />
                  </div>

                  {/* Additional Notes Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <div className="flex items-center gap-2 mb-4">
                      <h3 className="text-sm font-semibold text-black tracking-wide uppercase">Additional Notes</h3>
                      <Tooltip
                        message="Record overages, SKU changes, or any receiving issues for this PO."
                        iconSize={16}
                      />
                    </div>
                    <TextInputBoxLarge
                      id="operations-note-body"
                      placeholder="Operations Note (Optional)"
                      onChange={(value) => handleInputChange("operationsNote", value)}
                      value={formData.operationsNote ?? ""}
                      maxLength={1200}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("operationsNote")}
                    />
                  </div>
                </>
              )}

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Submit Button */}
              <div className="flex items-center gap-4">
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={!isFormComplete}
                  onClick={() => setAction(!parentPurchaseOrder ? "FETCH" : "CREATE")}
                  type="submit"
                >
                  {!parentPurchaseOrder ? "Search" : "Create Child Purchase Order"}
                </button>

                {!isFormComplete && parentPurchaseOrder && <SubmissionTooltip formState="INCOMPLETE" />}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { CreateChildPurchaseOrderForm };
